<template>
  <div
    data-testid="chat-input-container"
    :class="[
      'widget-input-container',
      dragover ? 'drag-drop-zone' : '',
      shouldPassClicksToParent ? 'widget-input-container--click-through' : '',
    ]"
    :style="containerStyle"
    @drop.prevent="onDrop"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
  >
    <div class="widget-input-form">
      <div :class="['widget-chatbox-container', isOpened && 'opened']">
        <div :class="['widget-icons-container', isOpened && 'opened']">
          <!-- Refresh icon - only visible when opened -->
          <div
            v-if="isOpened && showRefresh"
            data-testid="chat-input-restart-button"
            :class="['icon-button', 'refresh', { disabled }]"
            role="button"
            :tabindex="disabled ? -1 : 0"
            :aria-disabled="disabled ? 'true' : 'false'"
            aria-label="Restart chat"
            @click="onRestartChat"
            @keydown.enter.prevent="onRestartChat"
            @keydown.space.prevent="onRestartChat"
          >
            <RefreshIcon />
          </div>

          <!-- Attach icon - only visible when opened -->
          <div v-if="isOpened && showFileUpload" class="attach icon-button" data-testid="chat-input-attach-button">
            <label for="file-input">
              <AttachmentIcon />
            </label>
            <input
              id="file-input"
              data-testid="chat-input-file-input"
              type="file"
              :accept="allowedMimeTypes.join(',')"
              @change="selectImage"
            />
          </div>
        </div>

        <!-- Input wrapper with send button inside -->
        <div class="widget-input-wrapper" :style="isOpened ? { width: inputWrapperWidth } : {}">
          <input
            ref="inputForm"
            v-model="message"
            data-testid="chat-input-message-field"
            type="text"
            :disabled="disabled"
            :readonly="landingMode"
            class="input-radius theme-font-main widget-input widget-input-single-line"
            :class="{ 'widget-input--landing': landingMode }"
            :style="inputStyle"
            :placeholder="placeHolderContent"
            :aria-label="placeHolderContent"
            @click="handleLandingInputClick"
            @keydown.enter.exact.prevent="onSubmit"
          />

          <!-- Send button inside input -->
          <div
            data-testid="chat-input-submit-button"
            class="icon-button submit"
            :class="{ enable: isSubmitEnabled, disabled: !isSubmitEnabled }"
            :style="actionStyle"
            role="button"
            :tabindex="isSubmitEnabled ? 0 : -1"
            :aria-disabled="isSubmitEnabled ? 'false' : 'true'"
            :aria-label="submitAriaLabel"
            @click="onSubmit"
            @keydown.enter.prevent="onSubmit"
            @keydown.space.prevent="onSubmit"
          >
            <SendMessageIcon :class="[isSubmitEnabled && 'enable']" height="20" width="20" />
          </div>
        </div>
      </div>

      <span v-if="message.length >= charsWhenShowLimit" class="message-limit">
        (<span :class="message.length > messageLengthLimit ? 'message-over-limit' : ''">{{ message.length }}</span
        >/{{ messageLengthLimit }})
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import AttachmentIcon from '../icons/AttachmentIcon.vue';
import SendMessageIcon from '@/components/icons/SendMessageIcon.vue';
import RefreshIcon from '../icons/RefreshIcon.vue';
import { analytics, GENERAL_EVENTS } from '@/common/logs';
import { parseErrorMessage } from '@/common/error';
import { useWidgetInputCustomisation } from '../composables/useWidgetInputCustomisation';
import { useOverlayCustomisation } from '../composables/useOverlayCustomisation';

interface Attachment {
  contentType: string;
  name: string;
  size: number;
  url: string;
}

export interface AttachmentSelected {
  file: File;
  attachment: Attachment;
}

// Props
interface Props {
  disabled?: boolean;
  showFileUpload?: boolean;
  showRefresh?: boolean;
  isOpened?: boolean;
  landingMode?: boolean;
  charsWhenShowLimit?: number;
  messageLengthLimit?: number;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  showFileUpload: true,
  showRefresh: true,
  isOpened: false,
  charsWhenShowLimit: 4000,
  messageLengthLimit: 5000,
});

// Emits
const emit = defineEmits<{
  (e: 'submit-message', message: string): void;
  (e: 'file-upload', attachment: AttachmentSelected): void;
  (e: 'restart-chat'): void;
  (e: 'typing', message: boolean): void;
  (e: 'landing-activate'): void;
}>();

// Customisation
const customisation = useWidgetInputCustomisation();
const overlayCustomisation = useOverlayCustomisation();

// State
const dragover = ref(false);
const message = ref('');
const inputForm = ref<HTMLInputElement | null>(null);
const allowedMimeTypes = ['application/pdf', 'image/jpeg', 'image/png'];

// Computed
const validMessage = computed(() => {
  return message.value.replace(/\s/g, '').length > 0 && message.value.length <= props.messageLengthLimit;
});

const isSubmitEnabled = computed(() => {
  if (props.disabled) {
    return false;
  }

  if (props.landingMode) {
    return true;
  }

  return validMessage.value;
});

const submitAriaLabel = computed(() => (props.landingMode ? 'Open chat' : 'Send message'));

const placeHolderContent = computed(() => {
  return customisation.placeholderText.value || 'Type your message here...';
});

const visibleIconsCount = computed(() => {
  let count = 0;
  if (props.isOpened && props.showRefresh) count++;
  if (props.isOpened && props.showFileUpload) count++;
  return count;
});

/** Disabled native controls do not bubble clicks; let the parent bar open the overlay. */
const shouldPassClicksToParent = computed(() => (props.disabled || props.landingMode) && !props.isOpened);

const inputWrapperWidth = computed(() => {
  const iconWidth = 32; // Each icon is 32px
  const gap = visibleIconsCount.value > 1 ? 12 : 0; // Gap between icons
  const totalWidth = visibleIconsCount.value * iconWidth + (visibleIconsCount.value > 0 ? gap : 0);
  return `calc(100% - ${totalWidth}px)`;
});

const containerStyle = computed(() => {
  const backgroundColor = overlayCustomisation.backgroundColor.value;

  return {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(props.isOpened ? { borderTopColor: backgroundColor } : {}),
  };
});

const inputStyle = computed(() => {
  const backgroundColor = customisation.backgroundColor.value;
  const borderColor = customisation.borderColor.value;

  return {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(borderColor ? { borderColor } : {}),
  };
});

const actionStyle = computed(() => {
  const color = customisation.actionColor.value;
  return color ? { color } : {};
});

// Methods
const selectImage = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (props.disabled || !target.files?.length) return;

  const MAX_FILE_SIZE = 5 * 1024 * 1044;

  try {
    const file = target.files[0];
    const attachment = {
      mime_type: file.type,
      contentType: file.type,
      name: file.name,
      size: file.size,
      url: window.URL.createObjectURL(file),
    };

    if (attachment.size > MAX_FILE_SIZE) {
      analytics.track({
        name: GENERAL_EVENTS.FILE_UPLOAD_SIZE_EXCEEDED,
        metadata: attachment,
      });

      // You'll need to implement a dialog system or use a custom one
      alert('File size cannot exceed 5 MB');
      return;
    }

    if (!isValidMimeType(attachment.contentType)) {
      alert('Invalid file type');
      return;
    }

    emit('file-upload', { file, attachment });
  } catch (error) {
    analytics.track({
      name: GENERAL_EVENTS.FILE_UPLOAD_ERROR,
      metadata: { error: parseErrorMessage(error) },
    });

    alert('Error occurred while uploading file, please try again or select another file.');
  }
};

const isValidMimeType = (str: string) => {
  return allowedMimeTypes.includes(str);
};

const onSubmit = () => {
  if (props.landingMode) {
    handleLandingActivate();
    return;
  }

  if (props.disabled || !validMessage.value) return;
  emit('submit-message', message.value);
  message.value = '';
};

const handleLandingActivate = () => {
  if (props.disabled) {
    return;
  }

  emit('landing-activate');
};

const handleLandingInputClick = () => {
  if (!props.landingMode) {
    return;
  }

  handleLandingActivate();
};

const onRestartChat = () => {
  if (props.disabled) return;
  emit('restart-chat');
};

const onDrop = (event: DragEvent) => {
  dragover.value = false;
  // Handle file drop if needed
};

const onDragOver = (event: DragEvent) => {
  dragover.value = true;
};

const onDragLeave = (event: DragEvent) => {
  dragover.value = false;
};

// Watchers
const hasAutoFocusedThisOpenSession = ref(false);

watch(
  () => props.isOpened,
  (isOpened) => {
    if (!isOpened) {
      hasAutoFocusedThisOpenSession.value = false;
    }
  }
);

watch(
  () => [props.isOpened, props.disabled, props.landingMode] as const,
  async ([isOpened, disabled, landingMode]) => {
    if (!isOpened || disabled || landingMode || hasAutoFocusedThisOpenSession.value) return;

    await nextTick();
    inputForm.value?.focus();
    hasAutoFocusedThisOpenSession.value = true;
  },
  { flush: 'post', immediate: true }
);

watch(message, () => {
  emit('typing', !!message.value.trim());
});
</script>

<style lang="scss" scoped>
.widget-input-container {
  position: relative;
  padding: 12px;
  border-top: 1px solid #e0e0e0;

  &--click-through {
    pointer-events: none;
  }
}

.widget-input-form {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.widget-chatbox-container {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end; // Fixed to the right
  position: relative;
}

.widget-icons-container {
  position: absolute;
  top: 0;
  left: 0;

  display: flex;
  align-items: center;
  gap: 12px;

  height: 100%;
  transform: translateX(-100%);

  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  .opened & {
    transform: translateX(0);
  }
}

.widget-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.widget-input {
  width: 100%;
  min-height: 32px;
  padding: 12px 32px 12px 12px; // Right padding for send button
  border: 1px solid #e0e0e0;
  outline: none;

  &::placeholder {
    color: v-bind('customisation.placeholderColor.value');
  }
}

.widget-input-single-line {
  height: 48px;
  resize: none;
  overflow: hidden;
}

.widget-input--landing {
  cursor: pointer;
}

.icon-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  white-space: nowrap;

  &.attach {
    label {
      cursor: pointer;
      display: flex;
      align-items: center;
    }

    input[type='file'] {
      display: none;
    }
  }

  &.refresh {
    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  &.submit {
    position: absolute;
    right: 8px;
    opacity: 0.5;

    &:hover {
      opacity: 0.8;
    }

    &.disabled {
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}

.enable {
  opacity: 1 !important;
}

.message-limit {
  font-size: 12px;
  color: #666;
  text-align: right;
}

.message-over-limit {
  color: #f44336;
}

.drag-drop-zone {
  border: 2px dashed #4caf50;
  background: #f0f8f0;
}
</style>
